import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { toast } from "sonner";
import { X, VideoCamera, VideoCameraSlash, Warning, Stop, ChatCircle, ShieldWarning } from "@phosphor-icons/react";
import ProctorChatPanel from "../../components/ProctorChatPanel";

const FRAME_POLL_MS = 4000;

export default function ProctorDashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [chatId, setChatId] = useState(null);

  const [endingSession, setEndingSession] = useState(null); // { aid, name }

  const load = () => api.get("/proctor/live").then((r) => setItems(r.data)).finally(() => setLoading(false));

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const endSession = (aid, name) => setEndingSession({ aid, name });

  const confirmEnd = async (reason) => {
    if (!endingSession) return;
    try {
      await api.post(`/proctor/attempts/${endingSession.aid}/end`, { reason });
      toast.success(`Exam ended for ${endingSession.name}`);
      setEndingSession(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to end exam"); }
  };

  const avgProgress = items.length
    ? `${Math.round(items.reduce((s, i) => s + (i.answered / Math.max(1, i.total)), 0) / items.length * 100)}%`
    : "—";
  const flagged = items.filter((s) => s.face_status && s.face_status !== "ok" && s.face_status !== "unknown").length;
  const withWarnings = items.filter((s) => (s.warning_count || 0) > 0).length;
  const paused = items.filter((s) => s.status === "paused").length;

  return (
    <div>
      <PageHeader
        overline="Live Monitoring"
        title="Proctor control room"
        subtitle="Real-time webcam feeds of in-progress exam attempts. Frames refresh every ~4 seconds."
      />

      <div className="grid grid-cols-2 md:grid-cols-6 border border-zinc-200 mb-6">
        <Stat label="Active sessions" value={items.length} />
        <Stat label="With camera" value={items.filter((s) => s.has_frame).length} />
        <Stat label="Flagged" value={flagged} tone={flagged ? "red" : "zinc"} />
        <Stat label="Warned" value={withWarnings} tone={withWarnings ? "amber" : "zinc"} />
        <Stat label="Paused" value={paused} tone={paused ? "amber" : "zinc"} />
        <Stat label="Avg progress" value={avgProgress} />
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-zinc-200 p-10 text-center text-sm text-zinc-500">
          No live attempts at the moment.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="proctor-grid">
          {items.map((s) => (
            <ParticipantCard
              key={s.attempt_id}
              session={s}
              onExpand={() => setExpandedId(s.attempt_id)}
              onChat={() => setChatId(s.attempt_id)}
              onEnd={() => endSession(s.attempt_id, s.student_name)}
            />
          ))}
        </div>
      )}

      {expandedId && (
        <ExpandedFeed
          session={items.find((x) => x.attempt_id === expandedId)}
          onClose={() => setExpandedId(null)}
          onOpenChat={() => { setChatId(expandedId); setExpandedId(null); }}
        />
      )}

      {chatId && (() => {
        const s = items.find((x) => x.attempt_id === chatId);
        if (!s) { setChatId(null); return null; }
        return (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="proctor-chat-modal" onClick={() => setChatId(null)}>
            <div className="w-full max-w-md h-[600px]" onClick={(e) => e.stopPropagation()}>
              <ProctorChatPanel
                attemptId={s.attempt_id}
                studentName={s.student_name}
                examTitle={s.exam_title}
                onAutoSubmitted={() => { setChatId(null); load(); }}
                onEnded={() => { setChatId(null); load(); }}
                onClose={() => setChatId(null)}
              />
            </div>
          </div>
        );
      })()}

      {endingSession && (
        <EndExamModal
          session={endingSession}
          onCancel={() => setEndingSession(null)}
          onConfirm={confirmEnd}
        />
      )}
    </div>
  );
}

function EndExamModal({ session, onCancel, onConfirm }) {
  const [reason, setReason] = useState("Ended by proctor due to policy violation");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Please provide a reason"); return; }
    setSaving(true);
    try { await onConfirm(reason.trim()); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onCancel} data-testid="end-exam-modal">
      <div className="bg-white max-w-md w-full border border-zinc-200" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-zinc-200 bg-red-50">
          <div className="text-[10px] uppercase tracking-[0.25em] text-red-700 font-semibold">Force-end exam</div>
          <div className="font-display text-lg font-bold text-red-900 mt-1">{session.name}</div>
          <div className="text-xs text-red-800/80 mt-1">The attempt will be graded with the answers submitted so far and marked with <span className="font-mono">submit_reason=&quot;ended_by_proctor&quot;</span>. This cannot be undone.</div>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">Reason (shown to student &amp; recorded)</div>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-red-600 focus:outline-none"
              data-testid="end-exam-reason"
              placeholder="e.g. Multiple face-off-frame violations after warnings"
            />
          </div>
          <div className="text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-200 p-2">
            The student will see a system message in their chat, the exam will submit immediately, and a violation entry will be recorded.
          </div>
        </div>
        <div className="px-5 py-3 border-t border-zinc-200 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-zinc-300 text-sm hover:border-zinc-900"
            data-testid="end-exam-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="end-exam-confirm-btn"
          >
            <Stop size={12} weight="fill" /> {saving ? "Ending…" : "End exam now"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ParticipantCard({ session, onExpand, onChat, onEnd }) {
  const [frame, setFrame] = useState(null);
  const [error, setError] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchFrame = async () => {
      try {
        const r = await api.get(`/proctor/attempts/${session.attempt_id}/frame`);
        if (!cancelled) { setFrame(r.data); setError(false); }
      } catch {
        if (!cancelled) setError(true);
      }
    };
    fetchFrame();
    timerRef.current = setInterval(fetchFrame, FRAME_POLL_MS);
    return () => { cancelled = true; clearInterval(timerRef.current); };
  }, [session.attempt_id]);

  const progress = Math.round((session.answered / Math.max(1, session.total)) * 100);
  const status = frame?.face_status || session.face_status || "unknown";
  const statusColor = {
    ok: "bg-green-500 text-green-700 border-green-300 bg-green-50",
    no_face: "bg-amber-500 text-amber-700 border-amber-300 bg-amber-50",
    multi_face: "bg-red-500 text-red-700 border-red-300 bg-red-50",
    error: "bg-zinc-400 text-zinc-600 border-zinc-300 bg-zinc-50",
    unknown: "bg-zinc-400 text-zinc-600 border-zinc-300 bg-zinc-50",
  }[status] || "";

  return (
    <div className="border border-zinc-200 bg-white overflow-hidden" data-testid={`proctor-card-${session.attempt_id}`}>
      <button
        onClick={onExpand}
        className="relative w-full aspect-video bg-zinc-900 group cursor-pointer"
        data-testid={`proctor-feed-${session.attempt_id}`}
        aria-label={`Expand feed for ${session.student_name}`}
      >
        {frame?.image ? (
          <img
            src={frame.image}
            alt={`${session.student_name} webcam`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-zinc-500 gap-2">
            {error ? <VideoCameraSlash size={30} /> : <VideoCamera size={30} />}
            <div className="text-[10px] uppercase tracking-[0.2em]">
              {error ? "Awaiting first frame…" : "Loading feed…"}
            </div>
          </div>
        )}

        {/* Live badge */}
        <div className="absolute top-2 left-2 inline-flex items-center gap-1.5 px-2 py-0.5 bg-black/60 backdrop-blur">
          <span className="w-1.5 h-1.5 bg-red-500 pulse-dot rounded-full" />
          <span className="text-[9px] uppercase tracking-[0.2em] text-white font-semibold">Live</span>
        </div>

        {/* Face status */}
        {status !== "unknown" && (
          <div className={`absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 border text-[9px] uppercase tracking-wider font-semibold ${statusColor.split(" ").slice(1).join(" ")}`}>
            {status === "multi_face" && <Warning size={10} weight="fill" />}
            {status.replace("_", " ")}
          </div>
        )}

        {/* Warning tally */}
        {(session.warning_count || 0) > 0 && (
          <div className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 bg-red-600 text-white text-[9px] uppercase tracking-wider font-semibold" data-testid={`proctor-warning-badge-${session.attempt_id}`}>
            <ShieldWarning size={10} weight="fill" /> {session.warning_count}/{session.warning_limit || 3} warnings
          </div>
        )}

        {/* Paused badge */}
        {session.status === "paused" && (
          <div className="absolute inset-0 bg-amber-500/85 flex flex-col items-center justify-center text-white" data-testid={`proctor-paused-badge-${session.attempt_id}`}>
            <div className="text-3xl">⏸</div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold mt-1">Paused</div>
            {session.pause_reason && (
              <div className="text-[9px] mt-1 px-3 text-center opacity-90 line-clamp-2">{session.pause_reason}</div>
            )}
          </div>
        )}

        {/* Overlay hover */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
          <span className="opacity-0 group-hover:opacity-100 text-white text-xs tracking-wider">Click to expand →</span>
        </div>
      </button>

      <div className="p-4">
        <div className="font-display text-base font-bold leading-tight truncate">{session.student_name}</div>
        <div className="text-xs text-zinc-500 truncate">{session.student_email}</div>
        <div className="text-xs text-zinc-700 mt-2 truncate"><span className="text-zinc-500">Exam:</span> {session.exam_title}</div>
        <div className="flex items-center justify-between text-xs mt-3 mb-1">
          <span className="text-zinc-500">Progress</span>
          <span className="font-mono-tab font-semibold">{session.answered}/{session.total}</span>
        </div>
        <div className="h-1 bg-zinc-200">
          <div className="h-1 bg-zinc-900" style={{ width: `${progress}%` }} />
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onChat(); }}
          className="mt-3 w-full inline-flex items-center justify-center gap-1.5 bg-zinc-900 text-white text-xs px-3 py-1.5 hover:bg-zinc-800"
          data-testid={`proctor-chat-btn-${session.attempt_id}`}
        >
          <ChatCircle size={11} weight="fill" /> Chat & warn
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onEnd(); }}
          className="mt-1.5 w-full inline-flex items-center justify-center gap-1.5 border border-red-300 text-red-700 text-xs px-3 py-1.5 hover:bg-red-50"
          data-testid={`proctor-end-btn-${session.attempt_id}`}
        >
          <Stop size={11} weight="fill" /> End session
        </button>
      </div>
    </div>
  );
}

function ExpandedFeed({ session, onClose, onOpenChat }) {
  const [frame, setFrame] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    const fetchFrame = async () => {
      try {
        const r = await api.get(`/proctor/attempts/${session.attempt_id}/frame`);
        if (!cancelled) setFrame(r.data);
      } catch { /* transient polling error */ }
    };
    fetchFrame();
    timerRef.current = setInterval(fetchFrame, 2000);
    return () => { cancelled = true; clearInterval(timerRef.current); };
  }, [session]);

  if (!session) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="proctor-expanded-modal" onClick={onClose}>
      <div className="bg-white max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Live feed</div>
            <div className="font-display text-lg font-bold">{session.student_name}</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onOpenChat}
              className="inline-flex items-center gap-1.5 bg-zinc-900 text-white px-3 py-1.5 text-xs hover:bg-zinc-800"
              data-testid="proctor-expanded-open-chat"
            >
              <ChatCircle size={12} weight="fill" /> Chat & warn
            </button>
            <button
              onClick={onClose}
              className="text-zinc-600 hover:text-zinc-900 p-2"
              data-testid="proctor-expanded-close"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="aspect-video bg-zinc-900">
          {frame?.image ? (
            <img src={frame.image} alt={`${session.student_name} live`} className="w-full h-full object-contain" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-zinc-500">Waiting for frame…</div>
          )}
        </div>
        <div className="grid grid-cols-3 border-t border-zinc-200">
          <div className="p-4 border-r border-zinc-200">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Exam</div>
            <div className="text-sm font-medium truncate">{session.exam_title}</div>
          </div>
          <div className="p-4 border-r border-zinc-200">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Progress</div>
            <div className="text-sm font-mono-tab font-semibold">{session.answered} / {session.total}</div>
          </div>
          <div className="p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Face status</div>
            <div className="text-sm font-medium">{(frame?.face_status || session.face_status || "unknown").replace("_", " ")}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "zinc" }) {
  const tones = {
    zinc: "text-zinc-900",
    red: "text-red-700",
    amber: "text-amber-700",
  };
  return (
    <div className="p-5 border-r border-zinc-200 last:border-r-0">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 font-semibold">{label}</div>
      <div className={`font-display text-2xl font-bold tabular ${tones[tone]}`}>{value}</div>
    </div>
  );
}
